<template>
  <PaginaAdministrar
    module-name="Logs de Erro (Bugs)"
    :loading="loading"
    :erro="erro"
    :mostrar-botao-adicionar="false"
    v-model:filtros-abertos="filtrosAbertos"
    :total-itens="paginacao.totalCount"
    :itens-por-pagina="paginacao.pageSize"
    :pagina-atual="paginacao.currentPage"
    @mudar-pagina="mudarPagina"
    @mudar-tamanho-pagina="mudarTamanhoPagina"
    @atualizar="aplicarFiltros"
  >
    <template #acoes="{ toggleFiltros }">
      <button class="btn btn-dark" @click="toggleFiltros">
        <i class="bi bi-filter-lg me-2"></i> Filtros
      </button>
    </template>

    <template #filtros>
      <div class="row g-3 align-items-end">
        <div class="col-md-3">
          <label class="form-label">Empresa (ID)</label>
          <input type="number" class="form-control" v-model.number="filtros.idEmpresa" placeholder="Filtrar por empresa" />
        </div>
        <div class="col-md-3">
          <label class="form-label">Origem</label>
          <select class="form-select" v-model="filtros.origem" @change="aplicarFiltros">
            <option value="">Todas</option>
            <option value="Backend">Backend</option>
            <option value="Frontend">Frontend</option>
          </select>
        </div>
        <div class="col-md-3">
          <label class="form-label">Status Tratamento</label>
          <select class="form-select" v-model="filtros.statusTratamento" @change="aplicarFiltros">
            <option value="">Todos</option>
            <option value="SemTratamento">Sem tratamento</option>
            <option value="Aberto">Aberto</option>
            <option value="EmTratamento">Em tratamento</option>
            <option value="Finalizado">Finalizado</option>
            <option value="Cancelado">Cancelado</option>
          </select>
        </div>
        <div class="col-md-3">
          <CampoDataIntervalo
            id="filtroDataCriacao"
            label="Período do erro"
            v-model:modelValueInicio="filtros.dataInicio"
            v-model:modelValueFim="filtros.dataFim"
          />
        </div>
        <div class="col-12 text-end">
          <button class="btn btn-secondary me-2" @click="limparFiltros">Limpar</button>
          <button class="btn btn-primary" @click="aplicarFiltros">Aplicar Filtros</button>
        </div>
      </div>
    </template>

    <AdminTable
      :columns="colunas"
      :data="logs"
      :loading="loading"
      :sort-field="sortField"
      :sort-order="sortOrder"
      @sort="aoOrdenar"
    >
      <template #tsCriacao="{ row }">
        <span class="small">{{ formatarDataHoraBR(row.tsCriacao) }}</span>
      </template>
      <template #exceptionType="{ row }">
        <code class="small">{{ row.exceptionType }}</code>
      </template>
      <template #message="{ row }">
        <span class="text-truncate d-inline-block" style="max-width: 300px;" :title="row.message">
          {{ row.message }}
        </span>
      </template>
      <template #origem="{ row }">
        <span :class="['badge', row.origem === 'Backend' ? 'badge-ghost-primary' : 'badge-ghost-info']">
          {{ row.origem }}
        </span>
      </template>
      <template #statusTratamento="{ row }">
        <span v-if="row.tratamento" :class="statusBadgeClass(row.tratamento.status)">
          {{ row.tratamento.status }}
        </span>
        <span v-else class="text-muted small">—</span>
      </template>
      <template #acoes="{ row }">
        <button type="button" class="btn btn-sm btn-outline-info me-1" @click="verDetalhe(row)" title="Ver detalhes">
          <i class="bi bi-eye"></i>
        </button>

        <!-- Sem tratamento → criar novo -->
        <button
          v-if="!row.tratamento"
          type="button"
          class="btn btn-sm btn-outline-success"
          @click="iniciarTratamento(row)"
          title="Tratar"
        >
          <i class="bi bi-bug"></i>
        </button>

        <!-- Com tratamento → visualizar (read-only se Finalizado/Cancelado, editável se Aberto/EmTratamento) -->
        <button
          v-else
          type="button"
          class="btn btn-sm btn-outline-warning"
          @click="abrirTratamento(row.tratamento.idErrorLogTratamento)"
          :title="'Visualizar tratamento (' + row.tratamento.status + ')'"
        >
          <i class="bi bi-pencil-square"></i>
        </button>
      </template>
    </AdminTable>

    <!-- Modal de Detalhe do Log (z-index maior que o modal de tratamento) -->
    <ModalFormularioV2
      v-model="modalDetalheVisivel"
      :titulo="'Detalhe do Erro #' + (detalhe?.idErrorLog || '')"
      tamanho="xl"
      :loading="false"
      :z-index="10060"
    >
      <div class="row g-3" v-if="detalhe">
        <div class="col-md-6">
          <label class="form-label small fw-bold">Tipo</label>
          <p class="mb-0"><code>{{ detalhe.exceptionType }}</code></p>
        </div>
        <div class="col-md-6">
          <label class="form-label small fw-bold">Data/Hora</label>
          <p class="mb-0">{{ formatarDataHoraBR(detalhe.tsCriacao) }}</p>
        </div>
        <div class="col-12">
          <label class="form-label small fw-bold">Mensagem</label>
          <textarea class="form-control" rows="2" readonly :value="detalhe.message" style="resize: none;"></textarea>
        </div>
        <div class="col-md-4">
          <label class="form-label small fw-bold">Controller</label>
          <p class="mb-0">{{ detalhe.controller || '-' }}</p>
        </div>
        <div class="col-md-4">
          <label class="form-label small fw-bold">Action</label>
          <p class="mb-0">{{ detalhe.action || '-' }}</p>
        </div>
        <div class="col-md-4">
          <label class="form-label small fw-bold">HTTP Method</label>
          <p class="mb-0">{{ detalhe.httpMethod || '-' }}</p>
        </div>
        <div class="col-12">
          <label class="form-label small fw-bold">Path</label>
          <p class="mb-0"><code>{{ detalhe.requestPath || '-' }}</code></p>
        </div>
        <div class="col-md-4">
          <label class="form-label small fw-bold">Empresa</label>
          <p class="mb-0">{{ detalhe.idEmpresa || '-' }}</p>
        </div>
        <div class="col-md-4">
          <label class="form-label small fw-bold">Usuário</label>
          <p class="mb-0">{{ detalhe.nomeUsuario || detalhe.idUsuario || '-' }}</p>
        </div>
        <div class="col-md-4">
          <label class="form-label small fw-bold">IP</label>
          <p class="mb-0"><code>{{ detalhe.clientIp || '-' }}</code></p>
        </div>
        <div class="col-12">
          <label class="form-label small fw-bold">User Agent</label>
          <textarea class="form-control font-monospace small" rows="2" readonly :value="detalhe.userAgent || '-'" style="resize: vertical; min-height: 50px;"></textarea>
        </div>

        <!-- Payload (Mascarado) -->
        <div class="col-12">
          <label class="form-label small fw-bold">Payload (Mascarado)</label>
          <textarea
            class="form-control font-monospace small"
            rows="4"
            readonly
            :value="detalhe.requestBodyMasked || '(vazio)'"
            style="resize: vertical; min-height: 80px;"
          ></textarea>
        </div>

        <!-- Stack Trace -->
        <div class="col-12">
          <label class="form-label small fw-bold">Stack Trace</label>
          <textarea
            class="form-control font-monospace small"
            rows="8"
            readonly
            :value="detalhe.stackTrace || detalhe.message"
            style="resize: vertical; min-height: 120px;"
          ></textarea>
        </div>

        <!-- Inner Exception -->
        <div class="col-12" v-if="detalhe.innerException">
          <label class="form-label small fw-bold">Inner Exception</label>
          <textarea
            class="form-control font-monospace small"
            rows="6"
            readonly
            :value="detalhe.innerException"
            style="resize: vertical; min-height: 100px;"
          ></textarea>
        </div>
      </div>
      <template #footer>
        <div class="d-flex gap-2 w-100 justify-content-end">
          <button type="button" class="btn btn-outline-secondary" @click="modalDetalheVisivel = false">
            Fechar
          </button>
          <button type="button" class="btn btn-info text-white" @click="copiarDetalheParaClipboard" title="Copiar para área de transferência">
            <i class="bi bi-clipboard me-1"></i>Copiar
          </button>
        </div>
      </template>
    </ModalFormularioV2>

    <!-- Modal de Tratamento -->
    <ModalFormularioV2
      v-model="modalTratamentoVisivel"
      :titulo="'Tratamento #' + (tratamento?.idErrorLogTratamento || '')"
      tamanho="lg"
      :loading="loadingTratamento"
    >
      <div class="row g-3" v-if="tratamento">
        <!-- Status e responsável -->
        <div class="col-md-4">
          <label class="form-label small fw-bold">Status</label>
          <p class="mb-0">
            <span :class="statusBadgeClass(tratamento.status)">{{ tratamento.status }}</span>
          </p>
        </div>
        <div class="col-md-4">
          <label class="form-label small fw-bold">Assumido por</label>
          <p class="mb-0">{{ tratamento.nomeUsuarioAssumiu || '-' }}</p>
        </div>
        <div class="col-md-4">
          <label class="form-label small fw-bold">Data assunção</label>
          <p class="mb-0">{{ tratamento.tsAssumido ? formatarDataHoraBR(tratamento.tsAssumido) : '-' }}</p>
        </div>
        <div class="col-md-4" v-if="tratamento.status === 'Finalizado'">
          <label class="form-label small fw-bold">Finalizado por</label>
          <p class="mb-0">{{ tratamento.nomeUsuarioFinalizou || '-' }}</p>
        </div>
        <div class="col-md-4" v-if="tratamento.status === 'Finalizado'">
          <label class="form-label small fw-bold">Data finalização</label>
          <p class="mb-0">{{ tratamento.tsFinalizado ? formatarDataHoraBR(tratamento.tsFinalizado) : '-' }}</p>
        </div>
        <div class="col-md-4" v-if="tratamento.status === 'Finalizado'">
          <label class="form-label small fw-bold">Classificação</label>
          <p class="mb-0">
            <span :class="classificacaoBadgeClass(tratamento.classificacao)">{{ tratamento.classificacao || '-' }}</span>
          </p>
        </div>

        <!-- Logs vinculados -->
        <div class="col-12">
          <label class="form-label small fw-bold">Logs vinculados ({{ tratamento.itens?.length || 0 }})</label>
          <div v-if="tratamento.itens && tratamento.itens.length > 0" class="table-responsive">
            <table class="table table-sm table-borderless small mb-0">
              <thead>
                <tr class="text-muted">
                  <th>ID</th>
                  <th>Tipo</th>
                  <th>Mensagem</th>
                  <th>Data</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in tratamento.itens" :key="item.idErrorLogTratamentoItem">
                  <td><code>{{ item.idErrorLog }}</code></td>
                  <td><code class="small">{{ item.exceptionType }}</code></td>
                  <td><span class="text-truncate d-inline-block" style="max-width: 250px;">{{ item.message }}</span></td>
                  <td>{{ formatarDataHoraBR(item.tsCriacao) }}</td>
                  <td>
                    <div class="d-flex gap-1">
                      <button
                        type="button"
                        class="btn btn-sm btn-outline-info py-0 px-1"
                        @click="verDetalhe(item)"
                        title="Ver detalhes"
                      >
                        <i class="bi bi-eye"></i>
                      </button>
                      <button
                        v-if="tratamento.status === 'EmTratamento'"
                        type="button"
                        class="btn btn-sm btn-outline-danger py-0 px-1"
                        @click="desvincularLog(item.idErrorLog)"
                        title="Desvincular"
                      >
                        <i class="bi bi-x"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <p v-else class="text-muted small mb-0">Nenhum log vinculado.</p>
        </div>

        <!-- Campo de busca para vincular logs (apenas em tratamento) -->
        <div class="col-12" v-if="tratamento.status === 'EmTratamento'">
          <label class="form-label small fw-bold">Vincular logs</label>
          <CampoBuscaMultiplo
            :key="'vincularLogs-' + versaoCampoBusca"
            id="vincularLogs"
            :configuracao-busca="configBuscaErrorLog"
            hide-label
            @selecionados="vincularLogsSelecionados"
          />
        </div>

        <!-- Observações (editor WYSIWYG) -->
        <div class="col-12">
          <label class="form-label small fw-bold">Observações</label>
          <EditorRichText
            v-if="tratamento.status === 'EmTratamento'"
            v-model="observacoesEdit"
            placeholder="Descreva o que foi feito, análise técnica, etc."
          />
          <div v-else class="bg-body-tertiary p-2 rounded small" style="max-height: 300px; overflow: auto;">
            <div v-html="tratamento.observacoes || '(sem observações)'"></div>
          </div>
        </div>

      </div>
      <!-- Footer do modal de tratamento -->
      <template #footer>
        <div v-if="confirmarFinalizacao" class="d-flex gap-2 w-100 justify-content-end">
          <div class="col-md-6 me-auto">
            <label class="form-label small fw-bold mb-0">Classificação</label>
            <select class="form-select form-select-sm" v-model="classificacaoFinalizar">
              <option value="Corrigido">Corrigido</option>
              <option value="Duplicado">Duplicado</option>
              <option value="NaoReproduzivel">Não reproduzível</option>
              <option value="Ambiente">Problema de ambiente</option>
              <option value="FalsoPositivo">Falso positivo</option>
            </select>
          </div>
          <button type="button" class="btn btn-outline-secondary" @click="confirmarFinalizacao = false">
            Cancelar
          </button>
          <button type="button" class="btn btn-success" @click="finalizarTratamento">
            <i class="bi bi-check-lg me-1"></i>Confirmar finalização
          </button>
        </div>
        <div v-else class="d-flex gap-2 w-100 justify-content-end">
          <button type="button" class="btn btn-outline-secondary" @click="modalTratamentoVisivel = false">
            Sair
          </button>
          <button
            v-if="tratamento.status === 'EmTratamento'"
            type="button"
            class="btn btn-outline-danger"
            @click="confirmarCancelarTratamento"
          >
            <i class="bi bi-x-circle me-1"></i>Cancelar Tratamento
          </button>
          <button
            v-if="tratamento.status === 'EmTratamento'"
            type="button"
            class="btn btn-outline-primary"
            @click="salvarObservacoes"
          >
            <i class="bi bi-save me-1"></i>Salvar
          </button>
          <button
            v-if="tratamento.status === 'EmTratamento'"
            type="button"
            class="btn btn-success"
            @click="confirmarFinalizacao = true"
          >
            <i class="bi bi-check-lg me-1"></i>Finalizar
          </button>
        </div>
      </template>
    </ModalFormularioV2>

    <!-- Confirmation Modal para cancelar tratamento -->
    <ConfirmationModal
      ref="confirmacaoCancelarRef"
      title="Cancelar Tratamento"
      message="Deseja realmente cancelar este tratamento?<br><br>Os logs vinculados serão <strong>liberados</strong> para novos tratamentos. As observações registradas serão <strong>preservadas</strong> para consulta futura."
      confirm-button-text="Sim, cancelar tratamento"
      cancel-button-text="Não, continuar"
      @confirmed="onConfirmacaoCancelar"
    />
  </PaginaAdministrar>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import api from '@/services/api'
import { formatarDataHoraBR } from '@/utils/useDataBR'
import PaginaAdministrar from '@/components/base/grid/PaginaAdministrar.vue'
import AdminTable from '@/components/base/grid/AdminTable.vue'
import ModalFormularioV2 from '@/components/base/modal/ModalFormularioV2.vue'
import CampoBuscaMultiplo from '@/components/base/campos/CampoBuscaMultiplo.vue'
import CampoDataIntervalo from '@/components/base/campos/CampoDataIntervalo.vue'
import EditorRichText from '@/components/base/formulario/EditorRichText.vue'
import ConfirmationModal from '@/components/base/modal/ConfirmationModal.vue'
import { configBuscaErrorLog } from '@/search-definitions/errorLogSearch'

const emit = defineEmits(['show-notification'])

const loading = ref(false)
const erro = ref(null)
const logs = ref([])
const sortField = ref('')
const sortOrder = ref('')
const filtrosAbertos = ref(false)
const filtros = reactive({
  idEmpresa: null,
  origem: '',
  dataInicio: '',
  dataFim: '',
  statusTratamento: ''
})
const paginacao = reactive({
  totalCount: 0,
  pageSize: 25,
  currentPage: 1,
  totalPages: 0
})
const detalhe = ref(null)
const modalDetalheVisivel = ref(false)

// Estado do tratamento
const tratamento = ref(null)
const modalTratamentoVisivel = ref(false)
const loadingTratamento = ref(false)
const observacoesEdit = ref('')
const confirmarFinalizacao = ref(false)
const classificacaoFinalizar = ref('Corrigido')
const versaoCampoBusca = ref(0)
const confirmacaoCancelarRef = ref(null)

const statusBadgeClass = (status) => {
  const map = {
    Aberto: 'badge bg-secondary',
    EmTratamento: 'badge bg-warning text-dark',
    Finalizado: 'badge bg-success',
    Cancelado: 'badge bg-danger'
  }
  return map[status] || 'badge bg-light text-dark'
}

const classificacaoBadgeClass = (classificacao) => {
  const map = {
    Corrigido: 'badge bg-success',
    Duplicado: 'badge bg-info text-dark',
    NaoReproduzivel: 'badge bg-secondary',
    Ambiente: 'badge bg-warning text-dark',
    FalsoPositivo: 'badge bg-danger'
  }
  return map[classificacao] || 'badge bg-light text-dark'
}

const colunas = computed(() => [
  { field: 'idErrorLog', label: 'ID', width: 60, sortable: true },
  { field: 'tsCriacao', label: 'Data', width: 150, sortable: true, slotName: 'tsCriacao' },
  { field: 'exceptionType', label: 'Tipo', width: 200, sortable: true, slotName: 'exceptionType' },
  { field: 'message', label: 'Mensagem', sortable: true, slotName: 'message' },
  { field: 'controller', label: 'Controller', width: 150, sortable: true },
  { field: 'origem', label: 'Origem', width: 90, sortable: true, slotName: 'origem' },
  { field: 'statusTratamento', label: 'Tratamento', width: 110, sortable: false, slotName: 'statusTratamento' },
  { field: 'acoes', label: '', width: 130, align: 'right', slotName: 'acoes', resizable: false, sortable: false }
])

const aoOrdenar = ({ field, order }) => {
  sortField.value = field
  sortOrder.value = order
}

const buscarDados = async (page) => {
  const pageNum = page || paginacao.currentPage
  loading.value = true
  erro.value = null
  try {
    const params = { page: pageNum, pageSize: paginacao.pageSize }
    if (filtros.idEmpresa) params.idEmpresa = filtros.idEmpresa
    if (filtros.origem) params.origem = filtros.origem
    if (filtros.dataInicio) params.dataInicio = new Date(filtros.dataInicio).toISOString()
    if (filtros.dataFim) params.dataFim = new Date(filtros.dataFim + 'T23:59:59').toISOString()
    if (filtros.statusTratamento) params.statusTratamento = filtros.statusTratamento

    const response = await api.get('/ErrorLogs', { params })
    logs.value = response.data

    const pagination = response.headers['x-pagination']
    if (pagination) {
      Object.assign(paginacao, JSON.parse(pagination))
    }
  } catch (error) {
    if (error.response?.status === 403) {
      logs.value = []
      paginacao.totalCount = 0
    } else {
      erro.value = 'Falha ao carregar logs de erro.'
      emit('show-notification', { message: 'Erro ao carregar logs de erro.', type: 'error' })
    }
  } finally {
    loading.value = false
  }
}

const aplicarFiltros = () => {
  buscarDados(1)
}

const limparFiltros = () => {
  filtros.idEmpresa = null
  filtros.origem = ''
  filtros.dataInicio = ''
  filtros.dataFim = ''
  filtros.statusTratamento = ''
  buscarDados(1)
}

const mudarPagina = (page) => {
  if (page >= 1 && page <= (paginacao.totalPages || 1)) {
    buscarDados(page)
  }
}

const mudarTamanhoPagina = (novoTamanho) => {
  paginacao.pageSize = novoTamanho
  buscarDados(1)
}

const verDetalhe = async (log) => {
  try {
    const response = await api.get(`/ErrorLogs/${log.idErrorLog}`)
    detalhe.value = response.data
    modalDetalheVisivel.value = true
  } catch (error) {
    if (error.response?.status === 403) return
    emit('show-notification', { message: 'Erro ao carregar detalhe do log.', type: 'error' })
  }
}

const copiarDetalheParaClipboard = () => {
  if (!detalhe.value) return

  const d = detalhe.value
  const linhas = [
    `# Detalhe do Erro #${d.idErrorLog}`,
    '',
    `**Tipo:** \`${d.exceptionType || '-'}\``,
    `**Data/Hora:** ${formatarDataHoraBR(d.tsCriacao)}`,
    '',
    '## Mensagem',
    d.message || '-',
    '',
    `**Controller:** ${d.controller || '-'}`,
    `**Action:** ${d.action || '-'}`,
    `**HTTP Method:** ${d.httpMethod || '-'}`,
    `**Path:** \`${d.requestPath || '-'}\``,
    '',
    `**Empresa:** ${d.idEmpresa || '-'}`,
    `**Usuário:** ${d.nomeUsuario || d.idUsuario || '-'}`,
    `**IP:** \`${d.clientIp || '-'}\``,
    `**User Agent:** ${d.userAgent || '-'}`,
    '',
    '## Payload (Mascarado)',
    d.requestBodyMasked || '(vazio)',
    '',
    '## Stack Trace',
    d.stackTrace || d.message || '-'
  ]

  if (d.innerException) {
    linhas.push('', '## Inner Exception', d.innerException)
  }

  const texto = linhas.join('\n')

  navigator.clipboard.writeText(texto).then(() => {
    emit('show-notification', { message: 'Detalhe copiado para a área de transferência.', type: 'success' })
  }).catch(() => {
    // Fallback para navegadores sem suporte a clipboard API
    const textarea = document.createElement('textarea')
    textarea.value = texto
    textarea.style.position = 'fixed'
    textarea.style.opacity = '0'
    document.body.appendChild(textarea)
    textarea.select()
    document.execCommand('copy')
    document.body.removeChild(textarea)
    emit('show-notification', { message: 'Detalhe copiado para a área de transferência.', type: 'success' })
  })
}

// --- Tratamento ---
const iniciarTratamento = async (log) => {
  loadingTratamento.value = true
  try {
    const response = await api.post('/ErrorLogs/tratamentos', { idErrorLog: log.idErrorLog })
    tratamento.value = response.data
    observacoesEdit.value = tratamento.value.observacoes || ''
    confirmarFinalizacao.value = false
    modalTratamentoVisivel.value = true
    await buscarDados(paginacao.currentPage)
  } catch (error) {
    if (error.response?.status === 403) return
    emit('show-notification', { message: 'Erro ao criar tratamento.', type: 'error' })
  } finally {
    loadingTratamento.value = false
  }
}

const abrirTratamento = async (idTratamento) => {
  loadingTratamento.value = true
  try {
    const response = await api.get(`/ErrorLogs/tratamentos/${idTratamento}`)
    tratamento.value = response.data
    observacoesEdit.value = tratamento.value.observacoes || ''
    confirmarFinalizacao.value = false
    modalTratamentoVisivel.value = true
  } catch (error) {
    if (error.response?.status === 403) return
    emit('show-notification', { message: 'Erro ao carregar tratamento.', type: 'error' })
  } finally {
    loadingTratamento.value = false
  }
}

const vincularLogsSelecionados = async (itens) => {
  if (!itens || itens.length === 0) return
  const ids = itens.map(i => i.idErrorLog)
  try {
    const response = await api.post(`/ErrorLogs/tratamentos/${tratamento.value.idErrorLogTratamento}/itens`, {
      idsErrorLog: ids
    })
    tratamento.value = response.data
    // Remonta o CampoBuscaMultiplo para limpar os chips após o vínculo
    versaoCampoBusca.value++
    await buscarDados(paginacao.currentPage)
    emit('show-notification', { message: `${ids.length} log(s) vinculado(s) com sucesso.`, type: 'success' })
  } catch (error) {
    emit('show-notification', { message: 'Erro ao vincular logs.', type: 'error' })
  }
}

const desvincularLog = async (idErrorLog) => {
  try {
    await api.delete(`/ErrorLogs/tratamentos/${tratamento.value.idErrorLogTratamento}/itens/${idErrorLog}`)
    tratamento.value.itens = tratamento.value.itens.filter(i => i.idErrorLog !== idErrorLog)
    emit('show-notification', { message: 'Log desvinculado.', type: 'success' })
  } catch (error) {
    emit('show-notification', { message: 'Erro ao desvincular log.', type: 'error' })
  }
}

const salvarObservacoes = async () => {
  try {
    const response = await api.put(`/ErrorLogs/tratamentos/${tratamento.value.idErrorLogTratamento}/observacoes`, {
      observacoes: observacoesEdit.value
    })
    tratamento.value = response.data
    emit('show-notification', { message: 'Observações salvas.', type: 'success' })
  } catch (error) {
    emit('show-notification', { message: 'Erro ao salvar observações.', type: 'error' })
  }
}

const finalizarTratamento = async () => {
  try {
    const response = await api.post(`/ErrorLogs/tratamentos/${tratamento.value.idErrorLogTratamento}/finalizar`, {
      classificacao: classificacaoFinalizar.value,
      observacoes: observacoesEdit.value
    })
    tratamento.value = response.data
    confirmarFinalizacao.value = false
    await buscarDados(paginacao.currentPage)
    emit('show-notification', { message: 'Tratamento finalizado com sucesso.', type: 'success' })
  } catch (error) {
    emit('show-notification', { message: 'Erro ao finalizar tratamento.', type: 'error' })
  }
}

const confirmarCancelarTratamento = () => {
  confirmacaoCancelarRef.value?.show()
}

const onConfirmacaoCancelar = async (confirmou) => {
  if (!confirmou) return
  try {
    const response = await api.post(`/ErrorLogs/tratamentos/${tratamento.value.idErrorLogTratamento}/cancelar`)
    tratamento.value = response.data
    await buscarDados(paginacao.currentPage)
    emit('show-notification', { message: 'Tratamento cancelado.', type: 'info' })
  } catch (error) {
    emit('show-notification', { message: 'Erro ao cancelar tratamento.', type: 'error' })
  }
}

defineExpose({ buscarDados })

onMounted(() => {
  buscarDados(1)
})
</script>
